import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DOCUMENT_CATALOG,
  EXPERIENCE_LEVELS,
  EXPERIENCE_LEVEL_LABELS,
  JOB_CATEGORIES,
  JOB_CATEGORY_LABELS,
  JOB_REQUEST_STATUSES,
  JOB_ROLE_CATALOG,
  LICENSE_CATEGORY_LABELS,
  REQUIRED_DOCUMENT_KINDS,
  type ExperienceLevel,
  type JobCategory,
  type JobRequest,
  type JobRequestListQuery,
  type JobRequestStatus,
  type JobSelection,
  type LicenseCategory,
  type PaginatedResponse,
  type RequestDocument,
  type UserDocumentStatus,
} from '@driverforce/shared-types';
import { api } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { Forbidden } from '../components/Forbidden';
import { DocumentPreviewModal, type PreviewTarget } from '../components/DocumentPreviewModal';
import { toastApiError } from '../lib/apiToast';

type Filters = {
  status: JobRequestStatus | '';
  category: JobCategory | '';
  role: string;
  country_code: string;
  experience_level: ExperienceLevel | '';
  q: string;
  location: string;
};

const INITIAL_FILTERS: Filters = {
  status: '',
  category: '',
  role: '',
  country_code: '',
  experience_level: '',
  q: '',
  location: '',
};

const encodeRoleOption = (category: JobCategory, role?: string): string =>
  role ? `cat:${category}|role:${role}` : `cat:${category}`;

const decodeRoleOption = (
  v: string,
): { category: JobCategory | ''; role: string } => {
  if (!v) return { category: '', role: '' };
  const m = /^cat:([^|]+)(?:\|role:(.*))?$/.exec(v);
  if (!m) return { category: '', role: '' };
  const category = m[1] as JobCategory;
  if (!(JOB_CATEGORIES as readonly string[]).includes(category)) {
    return { category: '', role: '' };
  }
  return { category, role: m[2] ?? '' };
};

const PAGE_SIZE = 24;

type Page = {
  items: JobRequest[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
};

type ViewState =
  | { kind: 'loading' }
  | { kind: 'ready'; page: Page }
  | { kind: 'error' }
  | { kind: 'forbidden' };

const formatCreatedAt = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
};

const formatRelative = (iso: string): string => {
  const d = new Date(iso).getTime();
  if (Number.isNaN(d)) return '';
  const diff = Date.now() - d;
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 30) return `${days}d ago`;
  return formatCreatedAt(iso);
};

const capitalize = (s: string) => (s.length > 0 ? s[0].toUpperCase() + s.slice(1) : s);

const initialsOf = (name: string): string => {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? '').join('') || '·';
};

const formatLicenseCategories = (cats: readonly LicenseCategory[] | undefined): string =>
  !cats || cats.length === 0
    ? ''
    : cats.map((c) => LICENSE_CATEGORY_LABELS[c] ?? c).join(' + ');

const describeDocument = (doc: RequestDocument): { title: string; german: string; english: string; details: string[] } => {
  const entry = DOCUMENT_CATALOG[doc.kind];
  const german = entry?.germanName ?? doc.kind;
  const english = entry?.translationKey ?? '';
  const details: string[] = [];
  if (doc.subtype) details.push(doc.subtype);
  if (doc.issuing_country) details.push(`from ${doc.issuing_country}`);
  if (doc.expires_on) details.push(`exp ${doc.expires_on}`);
  if (doc.license_categories && doc.license_categories.length > 0) {
    details.push(formatLicenseCategories(doc.license_categories));
  }
  if (doc.first_registered_de) details.push(`DE ${doc.first_registered_de}`);
  return { title: german, german, english, details };
};

// Four-state view used by the admin roster:
//   missing          → nothing uploaded (red)
//   partial_upload   → some files uploaded but required kinds still missing (red-warn)
//   partial_verified → all required kinds uploaded, some still pending/rejected (amber)
//   complete         → every required kind uploaded AND approved (green)
//
// Falls back to the request-time `documents` draft (upload-presence only) when
// the request has no linked user yet — anonymous submissions can't be verified.
const docsVerification = (
  uploaded: JobRequest['uploaded_documents'],
  draft: RequestDocument[] | null,
): {
  state: 'complete' | 'partial_verified' | 'partial_upload' | 'missing';
  label: string;
} => {
  const required = REQUIRED_DOCUMENT_KINDS;
  // Latest status per required kind (uploaded_documents is ordered ASC, last wins).
  const statusByKind = new Map<string, UserDocumentStatus>();
  for (const d of uploaded ?? []) statusByKind.set(d.kind, d.status);

  // Anonymous request — no linked user yet. Fall back to the draft to at
  // least distinguish "none claimed" from "claimed N kinds".
  if (!uploaded) {
    const claimed = new Set((draft ?? []).map((d) => d.kind));
    if (claimed.size === 0) return { state: 'missing', label: 'None uploaded' };
    const missing = required.filter((k) => !claimed.has(k)).length;
    if (missing === 0) return { state: 'partial_verified', label: 'Awaiting review' };
    return { state: 'partial_upload', label: `${missing} required missing` };
  }

  if (statusByKind.size === 0) return { state: 'missing', label: 'None uploaded' };

  const missingRequired = required.filter((k) => !statusByKind.has(k));
  if (missingRequired.length > 0) {
    return { state: 'partial_upload', label: `${missingRequired.length} required missing` };
  }

  const approved = required.filter((k) => statusByKind.get(k) === 'approved').length;
  if (approved === required.length) {
    return { state: 'complete', label: 'All verified' };
  }
  return { state: 'partial_verified', label: `${approved}/${required.length} verified` };
};

const emailVerification = (
  req: JobRequest,
): { state: 'verified' | 'pending' | 'none'; label: string } => {
  if (!req.email) return { state: 'none', label: 'No email' };
  const lu = req.linked_user;
  // Verified if the user proved email control — either via an IdP (Google)
  // or by setting a password (which requires the same OTP flow).
  if (lu && (lu.email_verified || lu.signup_method === 'idp' || lu.has_password)) {
    return { state: 'verified', label: 'Verified' };
  }
  return { state: 'pending', label: 'Pending OTP' };
};

const buildQueryString = (filters: Filters, page: number, pageSize: number): string => {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.category) params.set('category', filters.category);
  if (filters.role.trim()) params.set('role', filters.role.trim());
  if (filters.country_code.trim()) {
    params.set('country_code', filters.country_code.trim().toUpperCase());
  }
  if (filters.experience_level) params.set('experience_level', filters.experience_level);
  if (filters.q.trim()) params.set('q', filters.q.trim());
  if (filters.location.trim()) params.set('location', filters.location.trim());
  params.set('page', String(page));
  params.set('pageSize', String(pageSize));
  return params.toString();
};

const STATUS_TONE: Record<JobRequestStatus, string> = {
  new: 'pill-amber',
  in_review: 'pill-ink',
  matched: 'pill-ok',
  notified: 'pill-ok',
  closed: 'pill',
};

export const RequestsPage: React.FC = () => {
  const { user, token } = useAuth();
  const isAdmin = user?.roles.includes('admin') ?? false;
  const [state, setState] = useState<ViewState>({ kind: 'loading' });
  const [filters, setFilters] = useState<Filters>(INITIAL_FILTERS);
  const [debouncedQ, setDebouncedQ] = useState('');
  const [debouncedLocation, setDebouncedLocation] = useState('');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const toggleExpanded = useCallback((id: string) => {
    setExpandedId((curr) => (curr === id ? null : id));
  }, []);

  useEffect(() => {
    const h = window.setTimeout(() => setDebouncedQ(filters.q), 300);
    return () => window.clearTimeout(h);
  }, [filters.q]);
  useEffect(() => {
    const h = window.setTimeout(() => setDebouncedLocation(filters.location), 300);
    return () => window.clearTimeout(h);
  }, [filters.location]);

  useEffect(() => {
    setPage(1);
  }, [
    filters.status,
    filters.category,
    filters.role,
    filters.country_code,
    filters.experience_level,
    debouncedQ,
    debouncedLocation,
  ]);

  const requestIdRef = useRef(0);

  const load = useCallback(async () => {
    if (!isAdmin) {
      setState({ kind: 'forbidden' });
      return;
    }
    const myId = ++requestIdRef.current;
    setState({ kind: 'loading' });
    api.setToken(token);
    const qs = buildQueryString(
      { ...filters, q: debouncedQ, location: debouncedLocation },
      page,
      PAGE_SIZE,
    );
    const res = await api.get<PaginatedResponse<JobRequest>>(`/job-requests?${qs}`);
    if (myId !== requestIdRef.current) return;
    if (res.success && res.data) {
      setState({ kind: 'ready', page: res.data });
      return;
    }
    if (res.error?.code === 'FORBIDDEN') {
      setState({ kind: 'forbidden' });
      return;
    }
    toastApiError(res, 'Failed to load requests');
    setState({ kind: 'error' });
  }, [
    isAdmin,
    token,
    page,
    debouncedQ,
    debouncedLocation,
    filters.status,
    filters.category,
    filters.role,
    filters.country_code,
    filters.experience_level,
  ]);

  useEffect(() => {
    void load();
  }, [load]);

  const deleteRequest = useCallback(
    async (req: JobRequest) => {
      const ok = window.confirm(
        `Delete the request from ${req.full_name}?\n\nIf linked to a user account, that account (and uploaded documents) will also be deleted.\n\nThis cannot be undone.`,
      );
      if (!ok) return;
      setDeletingId(req.id);
      api.setToken(token);
      const res = await api.delete<{ deleted: { job_request_id: string; user_id: string | null } }>(
        `/job-requests/${req.id}`,
      );
      setDeletingId(null);
      if (toastApiError(res, 'Failed to delete request')) return;
      void load();
    },
    [token, load],
  );

  const resetFilters = () => {
    setFilters(INITIAL_FILTERS);
    setPage(1);
  };

  const totalPages = useMemo(() => {
    if (state.kind !== 'ready') return 1;
    return Math.max(1, state.page.totalPages);
  }, [state]);

  const stats = useMemo(() => {
    if (state.kind !== 'ready') return null;
    const items = state.page.items;
    const docsComplete = items.filter(
      (r) => docsVerification(r.uploaded_documents ?? null, r.documents).state === 'complete',
    ).length;
    const newCount = items.filter((r) => r.status === 'new').length;
    return {
      total: state.page.total,
      onPage: items.length,
      docsComplete,
      newCount,
    };
  }, [state]);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (filters.status) n++;
    if (filters.category) n++;
    if (filters.country_code.trim()) n++;
    if (filters.experience_level) n++;
    if (filters.q.trim()) n++;
    if (filters.location.trim()) n++;
    return n;
  }, [filters]);

  if (state.kind === 'forbidden') {
    return <Forbidden />;
  }

  return (
    <div className="grid grid-cols-12 gap-8">
      {/* Secondary left rail — quick filters */}
      <FilterRail
        filters={filters}
        onChangeStatus={(s) => setFilters((f) => ({ ...f, status: s }))}
        onChangeCategory={(c) =>
          setFilters((f) => ({ ...f, category: c, role: '' }))
        }
        onChangeExperience={(e) =>
          setFilters((f) => ({ ...f, experience_level: e }))
        }
        statusCounts={
          state.kind === 'ready'
            ? countBy(state.page.items, (r) => r.status)
            : {}
        }
        total={state.kind === 'ready' ? state.page.total : null}
      />

      {/* Main column */}
      <div className="col-span-12 lg:col-span-9 space-y-8">
      {/* Editorial intro strip */}
      <div className="grid grid-cols-12 gap-6 items-end">
        <div className="col-span-12 lg:col-span-7">
          <p className="font-mono text-[11px] uppercase tracking-widish text-muted">
            File · 01 · Inbound roster
          </p>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl lg:text-[44px] leading-[1.05] tracking-tightish">
            Every applicant,
            <span className="italic text-amber-brand"> one card at a time.</span>
          </h2>
        </div>
        <div className="col-span-12 lg:col-span-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCell label="Total" value={stats?.total ?? '—'} accent />
            <StatCell label="On page" value={stats?.onPage ?? '—'} />
            <StatCell label="New" value={stats?.newCount ?? '—'} />
            <StatCell label="Docs OK" value={stats?.docsComplete ?? '—'} />
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="border border-rule bg-white shadow-paper">
        <div className="flex items-center justify-between px-5 py-3 border-b border-rule">
          <div className="flex items-center gap-3">
            <span className="font-mono text-[11px] uppercase tracking-widish text-muted">
              Filter
            </span>
            {activeFilterCount > 0 && (
              <span className="pill pill-amber">
                <span className="dot" />
                {activeFilterCount} active
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={resetFilters}
            disabled={activeFilterCount === 0}
            className="text-[11px] font-mono uppercase tracking-widish text-muted hover:text-ink disabled:opacity-40"
          >
            Reset
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 p-5">
          <FieldSelect
            label="Status"
            value={filters.status}
            onChange={(v) => setFilters((f) => ({ ...f, status: v as JobRequestStatus | '' }))}
            options={[
              { value: '', label: 'All statuses' },
              ...JOB_REQUEST_STATUSES.map((s) => ({ value: s, label: capitalize(s) })),
            ]}
          />
          <FieldSelect
            label="Category / role"
            value={
              filters.category
                ? encodeRoleOption(filters.category, filters.role || undefined)
                : ''
            }
            onChange={(v) => {
              const d = decodeRoleOption(v);
              setFilters((f) => ({ ...f, category: d.category, role: d.role }));
            }}
            renderOptions={() => (
              <>
                <option value="">All categories</option>
                {JOB_CATEGORIES.map((cat) => (
                  <optgroup key={cat} label={JOB_CATEGORY_LABELS[cat]}>
                    <option value={encodeRoleOption(cat)}>
                      All {JOB_CATEGORY_LABELS[cat].toLowerCase()}
                    </option>
                    {JOB_ROLE_CATALOG[cat].roles.map((r) => (
                      <option key={r} value={encodeRoleOption(cat, r)}>
                        {r}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </>
            )}
          />
          <FieldSelect
            label="Experience"
            value={filters.experience_level}
            onChange={(v) =>
              setFilters((f) => ({ ...f, experience_level: v as ExperienceLevel | '' }))
            }
            options={[
              { value: '', label: 'All levels' },
              ...EXPERIENCE_LEVELS.map((e) => ({
                value: e,
                label: EXPERIENCE_LEVEL_LABELS[e].title,
              })),
            ]}
          />
          <FieldInput
            label="Country"
            placeholder="DE"
            maxLength={2}
            value={filters.country_code}
            onChange={(v) =>
              setFilters((f) => ({ ...f, country_code: v.toUpperCase() }))
            }
          />
          <FieldInput
            label="City / postal"
            placeholder="Berlin, 12161…"
            value={filters.location}
            onChange={(v) => setFilters((f) => ({ ...f, location: v }))}
            search
          />
          <FieldInput
            label="Name or email"
            placeholder="Search…"
            value={filters.q}
            onChange={(v) => setFilters((f) => ({ ...f, q: v }))}
            search
          />
        </div>
      </div>

      {state.kind === 'error' && (
        <div
          role="alert"
          className="border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 flex items-center justify-between"
        >
          <span>Could not load applicants.</span>
          <button
            type="button"
            onClick={() => void load()}
            className="underline"
          >
            Retry
          </button>
        </div>
      )}

      {/* Cards */}
      {state.kind === 'loading' && (
        <ul className="grid grid-cols-1 gap-3" aria-live="polite" aria-busy="true">
          {Array.from({ length: 6 }).map((_, i) => (
            <ApplicantSkeleton key={i} delay={i} />
          ))}
        </ul>
      )}

      {state.kind === 'ready' && state.page.items.length === 0 && (
        <div className="border border-dashed border-rule-strong py-20 text-center">
          <p className="font-display text-2xl text-ink">No applicants match.</p>
          <p className="mt-2 text-sm text-muted">Try widening the filter set.</p>
        </div>
      )}

      {state.kind === 'ready' && state.page.items.length > 0 && (
        <ul className="grid grid-cols-1 gap-3">
          {state.page.items.map((r, i) => (
            <ApplicantCard
              key={r.id}
              request={r}
              expanded={expandedId === r.id}
              onToggle={() => toggleExpanded(r.id)}
              onDelete={() => void deleteRequest(r)}
              deleting={deletingId === r.id}
              riseDelay={i}
            />
          ))}
        </ul>
      )}

      {/* Pagination */}
      {state.kind === 'ready' && (
        <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-muted pt-2">
          <div className="font-mono text-[11px] uppercase tracking-widish">
            {state.page.total === 0
              ? 'No results'
              : `Page ${state.page.page} / ${totalPages} · ${state.page.total} total`}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-1.5 border border-rule-strong text-xs font-mono uppercase tracking-widish hover:bg-ink hover:text-paper transition disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-muted"
            >
              ← Prev
            </button>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="px-3 py-1.5 border border-rule-strong text-xs font-mono uppercase tracking-widish hover:bg-ink hover:text-paper transition disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-muted"
            >
              Next →
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

// ---------- subcomponents ----------

const countBy = <T,>(arr: T[], key: (t: T) => string): Record<string, number> => {
  const out: Record<string, number> = {};
  for (const item of arr) {
    const k = key(item);
    out[k] = (out[k] ?? 0) + 1;
  }
  return out;
};

const FilterRail: React.FC<{
  filters: Filters;
  onChangeStatus: (s: JobRequestStatus | '') => void;
  onChangeCategory: (c: JobCategory | '') => void;
  onChangeExperience: (e: ExperienceLevel | '') => void;
  statusCounts: Record<string, number>;
  total: number | null;
}> = ({
  filters,
  onChangeStatus,
  onChangeCategory,
  onChangeExperience,
  statusCounts,
  total,
}) => {
  const railItem = (active: boolean) =>
    `w-full text-left flex items-center justify-between px-3 py-2 border-l-2 text-sm transition ${
      active
        ? 'border-amber-brand bg-paper-2 text-ink font-medium'
        : 'border-transparent text-muted hover:text-ink hover:bg-paper-2/60'
    }`;

  return (
    // Hidden below lg — every rail filter (status / category / experience) is
    // also available in the filter bar selects, so phones lose nothing.
    <aside className="hidden lg:block lg:col-span-3 lg:sticky lg:top-6 lg:self-start space-y-6">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-widish text-muted px-3 mb-2">
          Inbox
        </div>
        <div className="border border-rule bg-white">
          <button
            type="button"
            onClick={() => onChangeStatus('')}
            className={railItem(filters.status === '')}
          >
            <span>All applicants</span>
            <span className="font-mono text-xs tabular text-muted">
              {total ?? '—'}
            </span>
          </button>
          {JOB_REQUEST_STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => onChangeStatus(s)}
              className={railItem(filters.status === s)}
            >
              <span className="flex items-center gap-2">
                <span
                  aria-hidden
                  className={`w-1.5 h-1.5 rounded-full ${
                    s === 'new'
                      ? 'bg-amber-brand'
                      : s === 'matched' || s === 'notified'
                      ? 'bg-moss'
                      : s === 'in_review'
                      ? 'bg-ink'
                      : 'bg-rule-strong'
                  }`}
                />
                {capitalize(s).replace('_', ' ')}
              </span>
              <span className="font-mono text-xs tabular text-muted">
                {statusCounts[s] ?? 0}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="font-mono text-[10px] uppercase tracking-widish text-muted px-3 mb-2">
          Category
        </div>
        <div className="border border-rule bg-white">
          <button
            type="button"
            onClick={() => onChangeCategory('')}
            className={railItem(filters.category === '')}
          >
            <span>All categories</span>
          </button>
          {JOB_CATEGORIES.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => onChangeCategory(c)}
              className={railItem(filters.category === c)}
            >
              <span>{JOB_CATEGORY_LABELS[c]}</span>
              <span className="font-mono text-[10px] uppercase tracking-widish text-muted">
                {JOB_ROLE_CATALOG[c].subKey}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="font-mono text-[10px] uppercase tracking-widish text-muted px-3 mb-2">
          Experience
        </div>
        <div className="border border-rule bg-white">
          <button
            type="button"
            onClick={() => onChangeExperience('')}
            className={railItem(filters.experience_level === '')}
          >
            <span>Any level</span>
          </button>
          {EXPERIENCE_LEVELS.map((e) => (
            <button
              key={e}
              type="button"
              onClick={() => onChangeExperience(e)}
              className={railItem(filters.experience_level === e)}
            >
              <span>{EXPERIENCE_LEVEL_LABELS[e].title}</span>
            </button>
          ))}
        </div>
      </div>
    </aside>
  );
};

const StatCell: React.FC<{ label: string; value: React.ReactNode; accent?: boolean }> = ({
  label,
  value,
  accent,
}) => (
  <div
    className={`border border-rule px-3 py-3 bg-white ${
      accent ? 'shadow-paper' : ''
    }`}
  >
    <div className="font-mono text-[10px] uppercase tracking-widish text-muted">{label}</div>
    <div
      className={`mt-1 font-display tabular text-2xl tracking-tightish ${
        accent ? 'text-amber-brand' : 'text-ink'
      }`}
    >
      {value}
    </div>
  </div>
);

const FieldSelect: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  options?: { value: string; label: string }[];
  renderOptions?: () => React.ReactNode;
}> = ({ label, value, onChange, options, renderOptions }) => (
  <label className="flex flex-col gap-1">
    <span className="font-mono text-[10px] uppercase tracking-widish text-muted">{label}</span>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-none border border-rule bg-white px-2.5 py-2 text-sm focus:border-ink focus:ring-0"
    >
      {renderOptions
        ? renderOptions()
        : options?.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
    </select>
  </label>
);

const FieldInput: React.FC<{
  label: string;
  placeholder?: string;
  value: string;
  onChange: (v: string) => void;
  maxLength?: number;
  search?: boolean;
}> = ({ label, placeholder, value, onChange, maxLength, search }) => (
  <label className="flex flex-col gap-1">
    <span className="font-mono text-[10px] uppercase tracking-widish text-muted">{label}</span>
    <input
      type={search ? 'search' : 'text'}
      maxLength={maxLength}
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-none border border-rule bg-white px-2.5 py-2 text-sm focus:border-ink focus:ring-0"
    />
  </label>
);

const ApplicantCard: React.FC<{
  request: JobRequest;
  expanded: boolean;
  onToggle: () => void;
  onDelete: () => void;
  deleting: boolean;
  riseDelay: number;
}> = ({ request: r, expanded, onToggle, onDelete, deleting, riseDelay }) => {
  const emailV = emailVerification(r);
  const docsV = docsVerification(r.uploaded_documents ?? null, r.documents);
  const primaryRole = primaryRoleOf(r.jobs);
  const extraRoles = totalRolesOf(r.jobs);

  return (
    <li
      className="rise"
      style={{ animationDelay: `${Math.min(riseDelay, 12) * 30}ms` }}
    >
      <article
        className={`card-lift border bg-white shadow-paper ${
          expanded ? 'border-ink' : 'border-rule'
        }`}
      >
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-controls={`applicant-${r.id}`}
          className="w-full text-left px-4 py-3 sm:px-5"
        >
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-ink text-paper font-display text-base shrink-0">
              {initialsOf(r.full_name)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <div className="font-display text-lg text-ink leading-tight truncate">
                  {r.full_name}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`pill ${STATUS_TONE[r.status]}`}>
                    <span className="dot" />
                    {capitalize(r.status)}
                  </span>
                  <span
                    aria-hidden
                    className="text-muted transition-transform"
                    style={{ transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
                  >
                    ›
                  </span>
                </div>
              </div>
              <div className="text-xs text-muted truncate">
                {r.email ?? r.phone_e164 ?? '—'}
                <span className="font-mono uppercase tracking-widish">
                  {' · '}
                  {r.address.country_code ?? '—'} · {r.address.city ?? '—'} ·{' '}
                  {formatRelative(r.created_at)}
                </span>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
                <div className="min-w-0 max-w-full text-sm text-ink truncate">
                  {primaryRole ?? '—'}
                  <span className="text-xs text-muted">
                    {r.experience_level
                      ? ` · ${EXPERIENCE_LEVEL_LABELS[r.experience_level].title}`
                      : ''}
                    {extraRoles > 1 && ` · +${extraRoles - 1} more`}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-1.5 md:ml-auto">
                  <VerificationPill state={emailV.state} label={emailV.label} kind="email" />
                  <VerificationPill state={docsV.state} label={docsV.label} kind="docs" />
                </div>
              </div>
            </div>
          </div>
        </button>

        {expanded && (
          <div
            id={`applicant-${r.id}`}
            className="reveal border-t border-rule px-4 py-4 sm:px-5 bg-paper-2/60"
          >
            <ExpandedDetails request={r} onDelete={onDelete} deleting={deleting} />
          </div>
        )}
      </article>
    </li>
  );
};

const primaryRoleOf = (jobs: JobSelection[] | null): string | null => {
  if (!jobs || jobs.length === 0) return null;
  const first = jobs[0];
  const cat = JOB_ROLE_CATALOG[first.category]?.titleKey ?? first.category;
  const role = first.roles[0];
  return role ? `${cat} · ${role}` : cat;
};

const totalRolesOf = (jobs: JobSelection[] | null): number =>
  (jobs ?? []).reduce((n, j) => n + j.roles.length, 0);

type PillState =
  | 'verified'
  | 'pending'
  | 'none'
  | 'complete'
  | 'partial_upload'
  | 'partial_verified'
  | 'missing';

const VerificationPill: React.FC<{
  state: PillState;
  label: string;
  kind: 'email' | 'docs';
}> = ({ state, label, kind }) => {
  // Four tones, mapped intentionally so the docs column reads at-a-glance:
  //   ok    (green)  = nothing left to do
  //   warn  (yellow) = action half-done (review pending)
  //   amber (orange) = action started but blocked (uploads incomplete)
  //   bad   (red)    = action not started
  let cls = 'pill pill-bad';
  let icon = '✕';
  if (state === 'verified' || state === 'complete') {
    cls = 'pill pill-ok';
    icon = '✓';
  } else if (state === 'partial_verified' || state === 'pending') {
    cls = 'pill pill-warn';
    icon = '◐';
  } else if (state === 'partial_upload') {
    cls = 'pill pill-amber';
    icon = '◔';
  }
  return (
    <div className={`${cls} max-w-full`} role="status" aria-label={`${kind} ${label}`}>
      <span aria-hidden className="font-mono">
        {icon}
      </span>
      <span className="opacity-60">{kind}</span>
      <span className="truncate">{label}</span>
    </div>
  );
};

const BIO_PREVIEW_LINES = 6;

const ExpandedDetails: React.FC<{
  request: JobRequest;
  onDelete: () => void;
  deleting: boolean;
}> = ({ request, onDelete, deleting }) => {
  const [showFullBio, setShowFullBio] = useState(false);
  const bio = request.bio?.trim() ?? '';
  const bioLines = bio.length > 0 ? bio.split(/\r?\n/) : [];
  const bioOverflow = bioLines.length > BIO_PREVIEW_LINES;
  const visibleBio =
    !showFullBio && bioOverflow ? bioLines.slice(0, BIO_PREVIEW_LINES).join('\n') : bio;

  const documents = request.documents ?? [];
  const uploadedByKind = useMemo(() => {
    const map = new Map<string, NonNullable<JobRequest['uploaded_documents']>[number]>();
    for (const u of request.uploaded_documents ?? []) {
      if (!map.has(u.kind)) map.set(u.kind, u);
    }
    return map;
  }, [request.uploaded_documents]);

  const [previewFile, setPreviewFile] = useState<PreviewTarget | null>(null);

  const contact: Array<[string, string, boolean?]> = [
    ['Phone', [request.phone_e164, request.phone_country].filter(Boolean).join(' · ') || '—', true],
    [
      'Location',
      [request.address.country_code, request.address.city].filter(Boolean).join(' · ') || '—',
    ],
    ['Address', request.address.postal_address ?? '—'],
    [
      'Created',
      `${formatCreatedAt(request.created_at)} · upd ${formatCreatedAt(request.updated_at)}`,
      true,
    ],
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 text-sm">
      {/* Left — contact, roles, bio */}
      <div className="lg:col-span-5 space-y-4">
        <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1.5">
          {contact.map(([label, value, mono]) => (
            <Fragment key={label}>
              <dt className="font-mono text-[10px] uppercase tracking-widish text-muted pt-0.5">
                {label}
              </dt>
              <dd
                className={`text-ink break-words whitespace-pre-wrap ${mono ? 'font-mono tabular text-xs pt-0.5' : ''}`}
              >
                {value}
              </dd>
            </Fragment>
          ))}
        </dl>

        <div>
          <SectionLabel>Roles claimed</SectionLabel>
          {(request.jobs ?? []).length === 0 ? (
            <div className="text-muted">—</div>
          ) : (
            <ul className="flex flex-wrap gap-1.5">
              {(request.jobs ?? []).flatMap((j) =>
                j.roles.map((role) => (
                  <li
                    key={`${j.category}-${role}`}
                    className="border border-rule bg-white px-2 py-1 text-xs text-ink"
                    title={JOB_ROLE_CATALOG[j.category]?.titleKey ?? j.category}
                  >
                    {role}
                  </li>
                )),
              )}
            </ul>
          )}
        </div>

        {bio.length > 0 && (
          <div>
            <SectionLabel>Bio</SectionLabel>
            <pre className="whitespace-pre-wrap break-words font-sans text-ink leading-snug">
              {visibleBio}
              {!showFullBio && bioOverflow ? '…' : ''}
            </pre>
            {bioOverflow && (
              <button
                type="button"
                onClick={() => setShowFullBio((v) => !v)}
                className="mt-1 text-xs font-mono uppercase tracking-widish text-amber-brand hover:underline"
              >
                {showFullBio ? 'Show less' : 'Show more'}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Right — documents */}
      <div className="lg:col-span-7">
        <div className="flex items-baseline justify-between">
          <SectionLabel>Documents</SectionLabel>
          <span className="font-mono text-[10px] uppercase tracking-widish text-muted">
            {documents.length} item{documents.length === 1 ? '' : 's'}
          </span>
        </div>
        {documents.length === 0 ? (
          <div className="border border-dashed border-rule-strong p-3 text-center text-muted text-sm">
            No documents provided.
          </div>
        ) : (
          <ul className="border border-rule bg-white divide-y divide-rule">
            {documents.map((doc, i) => {
              const d = describeDocument(doc);
              const required = (REQUIRED_DOCUMENT_KINDS as readonly string[]).includes(doc.kind);
              const uploaded = uploadedByKind.get(doc.kind);
              const files = uploaded?.files ?? [];
              // Newest first — API returns oldest-first, so reverse to make the
              // latest re-upload the obvious one to review.
              const ordered = [...files].reverse();
              return (
                <li key={`${doc.kind}-${i}`} className="px-3 py-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 truncate">
                      <span className="text-ink font-medium">{d.german}</span>
                      {d.english && (
                        <span className="ml-1.5 text-xs italic text-muted">{d.english}</span>
                      )}
                      {required && (
                        <span className="ml-1.5 text-red-700" title="Required">
                          *
                        </span>
                      )}
                    </div>
                    <UploadStatusPill status={uploaded?.status ?? null} />
                  </div>
                  {(d.details.length > 0 || ordered.length > 0) && (
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                      {d.details.map((bit, j) => (
                        <span
                          key={j}
                          className="font-mono text-[10px] uppercase tracking-widish text-muted"
                        >
                          {bit}
                        </span>
                      ))}
                      {uploaded &&
                        ordered.map((f, idx) => {
                          const isLatest = idx === 0;
                          const versionLabel = isLatest
                            ? ordered.length > 1
                              ? 'Latest'
                              : 'v1'
                            : `v${ordered.length - idx}`;
                          return (
                            <button
                              key={f.id}
                              type="button"
                              onClick={() =>
                                setPreviewFile({
                                  docId: uploaded.id,
                                  fileId: f.id,
                                  name: f.original_filename ?? f.s3_key.split('/').pop() ?? 'file',
                                  contentType: f.content_type,
                                })
                              }
                              title={new Date(f.uploaded_at).toLocaleString()}
                              className={`inline-flex items-center gap-1 min-w-0 max-w-full ${
                                isLatest ? 'text-ink' : 'text-muted opacity-70'
                              }`}
                            >
                              <span className="font-mono text-[9px] uppercase tracking-widish border border-current px-1">
                                {versionLabel}
                              </span>
                              <span className="underline hover:no-underline truncate">
                                {f.original_filename ?? f.s3_key.split('/').pop() ?? f.id}
                              </span>
                              <span className="text-muted shrink-0">
                                {(f.byte_size / 1024).toFixed(0)} KB
                              </span>
                            </button>
                          );
                        })}
                    </div>
                  )}
                  {uploaded?.status === 'rejected' && uploaded.rejection_reason && (
                    <div className="mt-1 text-xs text-red-700">
                      Rejected: {uploaded.rejection_reason}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        <div className="mt-3 flex justify-end">
          <button
            type="button"
            onClick={onDelete}
            disabled={deleting}
            className="text-xs font-mono uppercase tracking-widish text-red-700 hover:underline disabled:opacity-50"
          >
            {deleting ? 'Deleting…' : 'Delete applicant'}
          </button>
        </div>
      </div>

      {previewFile && (
        <DocumentPreviewModal target={previewFile} onClose={() => setPreviewFile(null)} />
      )}
    </div>
  );
};

const UPLOAD_STATUS_LABEL: Record<UserDocumentStatus, string> = {
  unverified: 'Awaiting upload',
  pending_review: 'Uploaded',
  approved: 'Approved',
  rejected: 'Rejected',
};

const UPLOAD_STATUS_CLASS: Record<UserDocumentStatus, string> = {
  unverified: 'bg-gray-100 text-gray-600',
  pending_review: 'bg-amber-100 text-amber-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

const UploadStatusPill: React.FC<{ status: UserDocumentStatus | null }> = ({ status }) => {
  if (!status) {
    return (
      <span className="inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full bg-gray-100 text-gray-500">
        Not uploaded
      </span>
    );
  }
  return (
    <span
      className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full ${UPLOAD_STATUS_CLASS[status]}`}
    >
      {UPLOAD_STATUS_LABEL[status]}
    </span>
  );
};

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="font-mono text-[10px] uppercase tracking-widish text-muted mb-1">
    {children}
  </div>
);

const ApplicantSkeleton: React.FC<{ delay: number }> = ({ delay }) => (
  <li
    className="border border-rule bg-white shadow-paper px-5 py-4 rise"
    style={{ animationDelay: `${delay * 40}ms` }}
  >
    <div className="grid grid-cols-12 gap-3 items-center">
      <div className="col-span-12 md:col-span-4 flex items-center gap-3">
        <span className="w-11 h-11 shrink-0 shimmer" />
        <div className="flex-1 space-y-1.5">
          <span className="block h-3.5 w-2/3 shimmer" />
          <span className="block h-2.5 w-1/2 shimmer" />
          <span className="block h-2 w-1/3 shimmer" />
        </div>
      </div>
      <div className="col-span-12 md:col-span-3 space-y-1.5">
        <span className="block h-3 w-3/4 shimmer" />
        <span className="block h-2.5 w-1/2 shimmer" />
      </div>
      <div className="col-span-6 md:col-span-2">
        <span className="block h-5 w-24 rounded-full shimmer" />
      </div>
      <div className="col-span-6 md:col-span-2">
        <span className="block h-5 w-24 rounded-full shimmer" />
      </div>
      <div className="col-span-12 md:col-span-1 flex justify-end">
        <span className="block h-5 w-14 rounded-full shimmer" />
      </div>
    </div>
  </li>
);

export type _AdminListQuery = JobRequestListQuery;
